import BaseController from "./BaseController";
import Component from "../Component";
import JSONModel from "sap/ui/model/json/JSONModel";
import Localization from "sap/base/i18n/Localization";
import Theming from "sap/ui/core/Theming";
import MessageToast from "sap/m/MessageToast";
import Fragment from "sap/ui/core/Fragment";
import type SideNavigation from "sap/tnt/SideNavigation";
import type { SideNavigation$ItemSelectEvent } from "sap/tnt/SideNavigation";
import type ToolPage from "sap/tnt/ToolPage";
import type Control from "sap/ui/core/Control";
import type Popover from "sap/m/Popover";
import type Dialog from "sap/m/Dialog";
import type { Button$PressEvent } from "sap/m/Button";
import type { Link$PressEvent } from "sap/m/Link";

interface MeResponse {
	sub?: string;
	name?: string;
	email?: string;
}

interface ProfileViewModel {
	subject: string;
	name: string;
	email: string;
	roles: string[];
}

const ROUTE_FOR_KEY: Record<string, string> = {
	dashboard: "dashboard",
	organizations: "organizations",
	partners: "partners",
	"my-company": "myCompany",
	customers: "customers",
	"product-families": "productFamilies",
	products: "products",
	"price-lists": "pricing",
	orders: "orders",
	entitlements: "entitlements",
	deployments: "deployments",
	licenses: "licenses",
	statistics: "statistics",
	lrDeadLetters: "lrDeadLetters",
	promotions: "promotions",
	"portal-users": "portalUsers",
	"audit-log": "audit-log",
	"system-info": "systemInfo",
};

const KEY_FOR_ROUTE: Record<string, string> = Object.fromEntries(
	Object.entries(ROUTE_FOR_KEY).map(([k, v]) => [v, k])
);

/**
 * @namespace computec.b2b.portal.controller
 */
export default class App extends BaseController {
	private profileDialog: Dialog | null = null;

	public onInit(): void {
		this.getView().addStyleClass(this.getOwnerComponent().getContentDensityClass());
		this.getView().setModel(new JSONModel({ subject: "", name: "", email: "", roles: [] }), "me");

		// Language model — current UI language sterujący checkmarkiem w menu języków.
		// Pre-bootstrap script w index.html załadował zapisany localStorage.portalLang do
		// sap-ui-config.language; tutaj odczytujemy aktualny stan i mapujemy do dwóch wspieranych
		// języków. Browser default (np. "en-US") sprowadza się do prefixu "en".
		const current = Localization.getLanguage().substring(0, 2).toLowerCase();
		const lang = current === "pl" ? "pl" : "en";
		let darkMode = false;
		try {
			darkMode = window.localStorage.getItem("portalDark") === "1";
			if (!darkMode) {
				const m = document.cookie.match(/(?:^|; )portalDark=([^;]*)/);
				darkMode = !!(m && m[1] === "1");
				if (darkMode) { window.localStorage.setItem("portalDark", "1"); }
			}
		} catch { /* private mode */ }
		this.getView().setModel(new JSONModel({ lang, darkMode }), "ui");

		this.getRouter().attachRouteMatched((event) => {
			const routeName = event.getParameter("name");
			const key = KEY_FOR_ROUTE[routeName];
			if (!key) {
				return;
			}
			const sideNav = this.byId("sideNav") as unknown as { setSelectedKey: (k: string) => void };
			sideNav.setSelectedKey(key);
		});

		// ADR-017 §1.7 + §2: bootstrap kolejność = (1) auto-accept invitation jeśli jest token w
		// session (oba typy userów lądują tu z KC executeActionsEmail redirectu); (2) gate
		// licensed-only userów. Acceptance MUSI iść pierwsze — dla licensed usera response
		// niesie RedirectUrl (np. Luna landing), SPA robi hard nav out i JS się zatrzymuje
		// zanim gate odpali. Gate (NoPortalAccess view) zostaje jako fallback dla licensed
		// userów którzy trafili na portal bez tokena (refresh, direct nav).
		void this.getOwnerComponent().bootstrapped.then(() => this._bootstrapPostLogin());
	}

	private async _bootstrapPostLogin(): Promise<void> {
		const component = this.getOwnerComponent();
		// Gated before router init (Component): the gate screen is already shown and nothing else may run.
		if (component.getModel("user")?.getProperty("/portalGate")) {
			return;
		}
		await this._maybeAutoAcceptInvitation();
		// Reached only when acceptance did not leave the SPA; re-evaluate with the refreshed /me.
		const gate = component.applyPortalGate();
		if (gate) {
			this.getRouter().navTo(gate, {}, undefined, true);
		}
	}

	/**
	 * Staff invitations carry no e-mail token: the first Microsoft sign-in activates the account
	 * (spec 29 D-079). Called only while the /me user is Pending. Returns true when the account was activated.
	 */
	private async _tryActivateStaff(): Promise<boolean> {
		// The hint is a deployment-wide setting; only a sign-in that really went through the staff
		// identity provider (identity_provider claim of the access token) may try to activate.
		const oidc = this.getOwnerComponent().getOidc();
		const staffIdp = oidc?.staffIdpHint;
		if (!staffIdp || oidc?.getAccessTokenClaims()?.["identity_provider"] !== staffIdp) {
			return false;
		}
		try {
			await this.getOwnerComponent().getAuthFetch().json("/api/v1/users/me/activate-staff", { method: "POST" });
		} catch (err) {
			const httpErr = err as { status?: number; bodyText?: string } | undefined;
			console.warn(`[Portal] staff activation refused: HTTP ${httpErr?.status ?? "?"}: ${httpErr?.bodyText ?? ""}`);
			return false;
		}
		await this.getOwnerComponent().refreshUserModelAsync();
		this.getRouter().navTo("dashboard", {}, undefined, true);
		return true;
	}

	private async _maybeAutoAcceptInvitation(): Promise<void> {
		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const status = userModel?.getProperty("/status") as string | undefined;
		if (status !== "Pending") {
			return;
		}

		let invitationToken: string | null = null;
		try {
			invitationToken = window.sessionStorage.getItem(Component.SESSION_KEY_INVITATION_TOKEN);
		} catch {
			/* private mode */
		}

		if (!invitationToken) {
			// User trafił do portalu w Pending stanie bez tokenu w session (np. direct nav,
			// expired storage, dwie zakładki). Skieruj na landing — pokaże tokenInvalid state.
			if (await this._tryActivateStaff()) {
				return;
			}
			this.getRouter().navTo("acceptInvitation", {}, undefined, true);
			return;
		}

		const bundle = await this.getResourceBundle();
		try {
			// ADR-017 §1.7: response.redirectUrl niepuste tylko dla licensed-user (HasPortalAccess=false)
			// — wtedy hard-nav out do produktowego landingu (Luna). Dla portal usera = null → zostajemy.
			const resp = await this.getOwnerComponent().getAuthFetch().json<{ userId: string; redirectUrl: string | null }>(
				"/api/v1/users/me/accept-invitation",
				{
					method: "POST",
					body: JSON.stringify({ invitationToken })
				});
			try {
				window.sessionStorage.removeItem(Component.SESSION_KEY_INVITATION_TOKEN);
			} catch {
				/* ignore */
			}
			if (resp.redirectUrl) {
				window.location.assign(resp.redirectUrl);
				return;
			}
			MessageToast.show(bundle.getText("acceptInvitation.success") ?? "Account activated.");
			// Refresh /me — status powinien teraz być Active. Permissions już są w access tokenie
			// (KC role mapping), więc UI nie potrzebuje hard reloadu.
			await this.getOwnerComponent().refreshUserModelAsync();
			// Portal user po accept zostaje w SPA — bez tego nav URL dalej jest `#/accept-invitation`
			// i AcceptInvitation view renderuje się ze stale tokenem (już wyczyszczony z sessionStorage
			// powyżej), pokazując bezsensowny "Token invalid". Nawigujemy do dashboardu, żeby user
			// zobaczył faktyczny rezultat aktywacji.
			this.getRouter().navTo("dashboard", {}, undefined, true);
		} catch (err) {
			// Loguj szczegóły HTTP (status + body) — pomaga diagnostyce gdy backend rzuca np.
			// `user.invitation_keycloak_mismatch` po resecie KC.
			const httpErr = err as { status?: number; bodyText?: string } | undefined;
			if (httpErr?.status !== undefined) {
				console.warn(`[Portal] auto AcceptInvitation failed: HTTP ${httpErr.status}: ${httpErr.bodyText ?? ""}`);
			} else {
				console.warn("[Portal] auto AcceptInvitation failed:", err);
			}
			// NIE czyścimy sessionStorage — bez tokena AcceptInvitation view pokaże tokenInvalid
			// i user nie może retry'ować manualnie. Auto-accept zfailowało, niech ma drugą szansę
			// przez "Activate" button. Token wyczyścimy dopiero po jego success path (lub gdy
			// expired/not_found — wtedy onActivate sam ustawi tokenInvalid).
			this.getRouter().navTo("acceptInvitation", {}, undefined, true);
		}
	}

	/**
	 * Przełącza motyw między sap_horizon (jasny) a sap_horizon_dark (ciemny).
	 * Stan persystowany w localStorage.portalDark — pre-bootstrap script w index.html
	 * odczytuje go przed inicjalizacją UI5 żeby uniknąć FOUC przy odświeżeniu strony.
	 * Model ui>/darkMode jest dwukierunkowo zbindowany do pressed ToggleButton, więc
	 * UI5 już zaktualizował wartość zanim ten handler odpali.
	 */
	public onDarkModeToggle(): void {
		const uiModel = this.getView().getModel("ui") as JSONModel;
		// Jawny toggle — nie polegamy na two-way binding (event press odpala przed
		// aktualizacją modelu, więc odczyt po bindingu dawałby starą wartość).
		const newDark = !(uiModel.getProperty("/darkMode") as boolean);
		uiModel.setProperty("/darkMode", newDark);
		Theming.setTheme(newDark ? "sap_horizon_dark" : "sap_horizon");
		try {
			if (newDark) {
				window.localStorage.setItem("portalDark", "1");
				document.cookie = "portalDark=1; path=/; SameSite=Lax; max-age=31536000";
			} else {
				window.localStorage.removeItem("portalDark");
				document.cookie = "portalDark=; path=/; SameSite=Lax; max-age=0";
			}
		} catch { /* private mode */ }
	}

	/** Otwiera popover języków zakotwiczony na globe button w headerze. */
	public onLanguageMenuPress(event: Button$PressEvent): void {
		(this.byId("langPopover") as Popover).openBy(event.getSource());
	}

	/**
	 * Wybór języka (link w popoverze, klucz w customData "lang") — zapis do localStorage
	 * i pełen reload strony. Reload jest niezbędny, bo i18n bundle ładuje się raz przy
	 * starcie aplikacji; UI5 nie odświeża zbindowanych <c>{i18n>...}</c> textów po
	 * <c>Localization.setLanguage</c>. Reload daje też pewność, że pre-bootstrap script w
	 * <c>index.html</c> ustawi <c>sap-ui-config.language</c> przed inicjalizacją UI5.
	 */
	public onLanguageLinkPress(event: Link$PressEvent): void {
		const key = event.getSource().data("lang") as string | undefined;
		if (key !== "pl" && key !== "en") {
			return;
		}
		try {
			window.localStorage.setItem("portalLang", key);
		} catch {
			/* private mode → reload bez persystencji; URL param to fallback */
		}
		window.location.reload();
	}

	/**
	 * Otwiera dwujęzyczny podręcznik użytkownika w nowej karcie. Standalone strony HTML
	 * (poza SPA) mieszkają w /help/{pl,en}/index.html. Język wybieramy z aktualnego
	 * ustawienia UI5 (Localization) — prefix "pl" → polska wersja, w przeciwnym razie EN.
	 */
	public onHelpPress(): void {
		const lang = Localization.getLanguage().toLowerCase().startsWith("pl") ? "pl" : "en";
		window.open(`/help/${lang}/index.html`, "_blank", "noopener");
	}

	public onSideNavToggle(): void {
		const tool = this.byId("toolPage") as ToolPage;
		const willExpand = !tool.getSideExpanded();
		tool.setSideExpanded(willExpand);
		const sideNav = this.byId("sideNav") as SideNavigation;
		if (willExpand) {
			sideNav.removeStyleClass("portalNavCollapsed");
		} else {
			sideNav.addStyleClass("portalNavCollapsed");
		}
	}

	public onSideNavItemSelect(event: SideNavigation$ItemSelectEvent): void {
		const item = event.getParameter("item");
		const key = item?.getKey();
		const route = key ? ROUTE_FOR_KEY[key] : undefined;
		if (route) {
			this.getRouter().navTo(route);
		}
	}

	public onLogout(): void {
		void this.getOwnerComponent().getOidc()?.logout();
	}

	/** Klik w ikonę użytkownika — otwiera popover użytkownika (wzorzec SAP for Me). */
	public onUserMenuPress(): void {
		(this.byId("userPopover") as Popover).openBy(this.byId("userMenuBtn") as Control);
	}

	public onProfileLinkPress(): void {
		(this.byId("userPopover") as Popover).close();
		void this.openProfileDialog();
	}

	private async openProfileDialog(): Promise<void> {
		if (!this.profileDialog) {
			this.profileDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.ProfileDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.profileDialog);
		}
		await this.refreshProfile();
		this.profileDialog.open();
	}

	private async refreshProfile(): Promise<void> {
		const component = this.getOwnerComponent();
		const fetcher = component.getAuthFetch();
		try {
			const me = await fetcher.json<MeResponse>("/api/v1/me");
			const idClaims = component.getOidc()?.getIdTokenClaims() ?? {};
			const accessClaims = component.getOidc()?.getAccessTokenClaims() ?? {};
			const realmAccess = (accessClaims["realm_access"] ?? idClaims["realm_access"]) as { roles?: string[] } | undefined;
			const data: ProfileViewModel = {
				subject: me.sub ?? (accessClaims["sub"] as string) ?? (idClaims["sub"] as string) ?? "",
				name: me.name ?? (idClaims["name"] as string) ?? (idClaims["preferred_username"] as string) ?? (accessClaims["preferred_username"] as string) ?? "",
				email: me.email ?? (idClaims["email"] as string) ?? (accessClaims["email"] as string) ?? "",
				roles: realmAccess?.roles ?? []
			};
			(this.getView().getModel("me") as JSONModel).setData(data);
		} catch {
			// stays empty
		}
	}

	public formatRoles(roles: string[] | null | undefined): string {
		if (!roles || roles.length === 0) {
			return "—";
		}
		return roles.join(", ");
	}

	public onProfileDialogClose(): void {
		this.profileDialog?.close();
	}

	public onLogoutLinkPress(): void {
		(this.byId("userPopover") as Popover).close();
		this.onLogout();
	}

	public formatInitials(name: string | undefined): string {
		if (!name) {
			return "?";
		}
		const parts = name.split(/\s+/).filter(Boolean);
		if (parts.length === 0) {
			return "?";
		}
		const first = parts[0]?.[0] ?? "";
		const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
		return (first + last).toUpperCase();
	}
}
